import React, { memo, useMemo, useRef, useState } from 'react'

import { FileTypeGroup } from '../constants'
import { useFileUploadHandler } from '../hook'
import { useFileSizeHandler } from '../hook/useFileSizeHandler'
import {
  MultiFileInputItem,
  removeMultiFileItem,
  removeMultiFileItemAtIndex,
  upsertMultiFileItem,
} from '../services'
import { getUploadPercentage } from '../services/FileInputFieldService'
import { MultiFileInputProps, UploadResponse } from '../types'

import { FileInput } from './FileInput'

type Props = Omit<MultiFileInputProps, 'onChange' | 'multiple'> & {
  fileTypesGroup?: FileTypeGroup
  value: MultiFileInputItem[]
  onChange: (items: MultiFileInputItem[]) => void
  upload: (
    file: File,
    fileUploadProgress: (e: ProgressEvent | number) => void,
  ) => Promise<UploadResponse>
  shouldResetValueBeforeNextUpload?: boolean
  invalidFileIds?: string[]
  onFilesUpload?: (files: File[]) => void
}

export const MultiFileInputField = memo(
  ({
    fileTypesGroup = FileTypeGroup.ALL,
    value,
    onChange,
    upload,
    showFailureAlert,
    shouldResetValueBeforeNextUpload = false,
    invalidFileIds,
    onRemoveClick: onRemoveClickProp,
    onFilesUpload,
    ...rest
  }: Props) => {
    const fileIdsRef = useRef<number[]>([])

    // we need to value synced otherwise created closures in useFileUploadHandler.handleChange
    // won't have updated value because we are modifying each file one by one
    const valueRef = useRef(value)
    const changeValue = (newValue: MultiFileInputItem[]) => {
      onChange(newValue)
      valueRef.current = newValue
    }

    const [uploadPercentages, setUploadPercentage] = useState<
      Record<string, number>
    >({})

    const createUploadProgressHandler =
      (file: File) => (e: ProgressEvent | number) => {
        setUploadPercentage((percentages) => ({
          ...percentages,
          [file.name]: getUploadPercentage(e),
        }))
      }

    const handleFileUpload = useFileUploadHandler({
      fileIdsRef,
      fileTypesGroup,
      upload: async (file: File) => {
        const f = await upload(file, createUploadProgressHandler(file))
        setUploadPercentage(({ [file.name]: rr, ...others }) => ({ ...others }))
        return f
      },
      handleChange: (input, changedFileName) => {
        if (!changedFileName) {
          return
        }

        if (!input || input instanceof Error) {
          changeValue(removeMultiFileItem(valueRef.current, changedFileName))
          return
        }

        changeValue(
          upsertMultiFileItem(valueRef.current, input, changedFileName),
        )
      },
      showFailureAlert,
    })

    const handleChange = useFileSizeHandler({
      isSingleFileUpload: false,
      onValidFile: handleFileUpload,
      onFilesUpload,
    })

    const onRemoveClick = (idx?: number) => {
      changeValue(removeMultiFileItemAtIndex(valueRef.current, idx))
    }

    const resetMultiFileInputValue = () => {
      changeValue([])
    }

    const derivedValueProps = useMemo(() => {
      const res: {
        uploadProgress: number[]
        fileName: string[]
        thumbnail: string[]
        url: (string | undefined)[]
        isInvalid?: boolean[]
      } = {
        uploadProgress: [],
        fileName: [],
        thumbnail: [],
        url: [],
        isInvalid: [],
      }

      value.forEach(({ fileName, thumbnail, url, location }) => {
        res.fileName.push(fileName)
        res.thumbnail.push(thumbnail ?? '')
        res.url.push(url)
        res.uploadProgress.push(uploadPercentages[fileName] ?? 0)
        res.isInvalid?.push(
          location ? !!invalidFileIds?.includes(location) : false,
        )
      })
      return res
    }, [value, uploadPercentages, invalidFileIds])

    return (
      <FileInput
        {...rest}
        {...{
          multiple: true,
          onChange: (e) => {
            if (shouldResetValueBeforeNextUpload) {
              resetMultiFileInputValue()
            }

            handleChange(e)

            if (shouldResetValueBeforeNextUpload) {
              e.target.value = ''
            }
          },
          onRemoveClick: onRemoveClickProp ?? onRemoveClick,
          ...derivedValueProps,
          fileTypesGroup,
        }}
      />
    )
  },
)
